import type { Dispatch, SetStateAction } from "react";
import api from "../../../services/api";
import { formatByteSize } from "../../../utility/byteSizeUtils";
import { IAnexo } from "../../../utility/IAnexo";

import { ArquivoUploadInfo } from "../ComponenteArquivos/utils";
import { uploadMultipleFilesWithStateManagement as uploadMultiplosArquivos } from "../AbaAnexos/fileUploadHelpers";

export const TAMANHO_MAXIMO_ARQUIVO = 50 * 1024 * 1024;
const ATRASO_PADRAO_REMOCAO = 3000;

interface EnviarArquivoParametros {
    arquivo: File;
    indice: number;
    idProposta?: number;
    aoAtualizarProgresso: (indice: number, progresso: number) => void;
    aoConcluir: (indice: number, resposta: any) => void;
    aoFalhar: (indice: number, mensagem: string) => void;
}

const enviarArquivo = async ({
    arquivo,
    indice,
    idProposta,
    aoAtualizarProgresso,
    aoConcluir,
    aoFalhar
}: EnviarArquivoParametros) => {
    if (!idProposta) {
        aoFalhar(indice, "Identificador da demanda não informado");
        return { sucesso: false, arquivo };
    }

    const dados = new FormData();
    dados.append("arquivo", arquivo);

    try {
        const resposta = await api.post(
            `proposta/${idProposta}/anexos`,
            dados,
            {
                onUploadProgress: (evento: ProgressEvent) => {
                    if (!evento.total) {
                        return;
                    }
                    const percentual = Math.round((evento.loaded * 100) / evento.total);
                    aoAtualizarProgresso(indice, percentual);
                }
            }
        );

        aoConcluir(indice, resposta.data);
        return { sucesso: true, arquivo, dados: resposta.data };
    } catch (erro: any) {
        const mensagem = erro?.response?.data?.message || 
                        erro?.response?.data?.details || 
                        erro?.response?.data?.error || 
                        erro?.message || 
                        "Erro ao enviar arquivo";
        aoFalhar(indice, mensagem);
        return { sucesso: false, arquivo };
    }
};

const removerUploadPorIndice = (
    definirUploads: Dispatch<SetStateAction<ArquivoUploadInfo[]>>,
    indice: number,
    atraso: number,
    aoRemover?: () => void,
    arquivoAlvo?: File
) => {
    setTimeout(() => {
        definirUploads((anterior) => {
            if (arquivoAlvo) {
                return anterior.filter((item) => item.arquivo !== arquivoAlvo);
            }
            return anterior.filter((_, posicao) => posicao !== indice);
        });
        aoRemover?.();
    }, atraso);
};

export const enviarArquivoComGerenciamento = async ({
    arquivo,
    indice,
    idProposta,
    definirUploads,
    aoAdicionarAnexo,
    atrasoRemocao = ATRASO_PADRAO_REMOCAO
}: {
    arquivo: File;
    indice: number;
    idProposta?: number;
    definirUploads: Dispatch<SetStateAction<ArquivoUploadInfo[]>>;
    aoAdicionarAnexo?: (anexo: IAnexo) => void;
    atrasoRemocao?: number;
}) => {
    return enviarArquivo({
        arquivo,
        indice,
        idProposta,
        aoAtualizarProgresso: (posicao, percentual) => {
            definirUploads((anterior) =>
                anterior.map((item, idx) =>
                    idx === posicao ? { ...item, progresso: percentual } : item
                )
            );
        },
        aoConcluir: (posicao, resposta) => {
            definirUploads((anterior) =>
                anterior.map((item, idx) =>
                    idx === posicao ? { ...item, status: "sucesso", progresso: 100 } : item
                )
            );

            removerUploadPorIndice(definirUploads, posicao, atrasoRemocao, () => {
                const anexo = resposta?.result as IAnexo | undefined;
                if (anexo) {
                    aoAdicionarAnexo?.(anexo);
                }
            }, arquivo);
        },
        aoFalhar: (posicao, mensagem) => {
            definirUploads((anterior) =>
                anterior.map((item, idx) =>
                    idx === posicao ? { ...item, status: "erro", erro: mensagem } : item
                )
            );
        }
    });
};

export const enviarMultiplosArquivosComGerenciamento = async ({
    arquivos,
    idProposta,
    definirUploads,
    aoAdicionarAnexo,
    atrasoRemocao = ATRASO_PADRAO_REMOCAO
}: {
    arquivos: File[];
    idProposta?: number;
    definirUploads: Dispatch<SetStateAction<ArquivoUploadInfo[]>>;
    aoAdicionarAnexo?: (anexo: IAnexo) => void;
    atrasoRemocao?: number;
}) => {
    if (!arquivos.length) {
        return { sucesso: false, resultados: [] };
    }

    // Inicializa os uploads com progresso 0 e status enviando
    const novosUploads: ArquivoUploadInfo[] = arquivos.map(arquivo => ({
        arquivo,
        progresso: 0,
        status: "enviando"
    }));
    
    // Adiciona os novos uploads ao estado
    let indiceInicial = 0;
    definirUploads((anterior) => {
        indiceInicial = anterior.length;
        return [...anterior, ...novosUploads];
    });
    
    // Prepara os índices para cada arquivo
    const indices = arquivos.map((_, idx) => indiceInicial + idx);
    
    // Converte os arquivos para o formato esperado pelo helper
    const files = arquivos.map(arquivo => arquivo as File);
    
    // Utiliza o helper de upload múltiplo
    const resultado = await uploadMultiplosArquivos({
        files,
        indexes: indices,
        demandaId: idProposta,
        setUploadingFiles: definirUploads as any, // Adaptação de tipos
        removalDelay: atrasoRemocao,
        onSuccessCallback: (responseData) => {
            if (responseData?.result) {
                aoAdicionarAnexo?.(responseData.result);
            }
        }
    });
    
    return {
        sucesso: resultado.success,
        parcialmenteSucesso: resultado.partialSuccess,
        resultados: resultado.results.map(r => ({
            sucesso: r.success,
            arquivo: r.file,
            dados: r.data
        }))
    };
};

export const calcularTamanhoTotalAnexos = (anexos: IAnexo[]): string => {
    const total = anexos.reduce((acumulado, atual) => acumulado + (atual.tamanho || 0), 0);
    return formatByteSize(total);
};

export const listarAnexosPorProposta = async (idProposta?: number): Promise<IAnexo[]> => {
    if (!idProposta) {
        return [];
    }

    const resposta = await api.get(`proposta/${idProposta}/anexos`);
    const dados = Array.isArray(resposta.data?.result) ? resposta.data.result : [];
    return [...dados].reverse();
};

